/**
 * useLocalePeek — render text in one locale temporarily without persistently
 * switching i18n.language (so the rest of the page stays in the user's chosen
 * locale).
 *
 * Two helpers:
 *   • useRotatingLocale(intervalMs)  — auto-cycles `en → hi → mr → en …`
 *   • useManualPeek()                — exposes { current, cycle() }
 *
 * Both return the *active peek locale*. Components call useLocaleText() to
 * fetch a translated string for the peek locale without mutating global i18n.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import i18n from "@/i18n";

const LANGS = ["en", "hi", "mr"];

function tWithLang(key, lang) {
  // i18n.t supports an `lng` option that resolves against the loaded bundle
  // without changing the current language. Returns the EN fallback if a key
  // is missing in the requested locale.
  return i18n.t(key, { lng: lang });
}

export function useRotatingLocale(intervalMs = 4500) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const pauseRef = useRef(paused);
  pauseRef.current = paused;

  useEffect(() => {
    const id = setInterval(() => {
      if (pauseRef.current) return;
      setIdx((i) => (i + 1) % LANGS.length);
    }, intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);

  return {
    current: LANGS[idx],
    pause: () => setPaused(true),
    resume: () => setPaused(false),
  };
}

export function useManualPeek(initial = "en") {
  const [idx, setIdx] = useState(LANGS.indexOf(initial) === -1 ? 0 : LANGS.indexOf(initial));
  const cycle = useCallback(() => setIdx((i) => (i + 1) % LANGS.length), []);
  return { current: LANGS[idx], cycle };
}

export function localeText(key, lang) {
  return tWithLang(key, lang);
}

export const LOCALE_LABELS = { en: "EN", hi: "हिं", mr: "मरा" };
